/**
 * Filiz Kozmetik API İstek Yöneticisi
 * 
 * Bu modül API isteklerini yönetir, önbelleğe alır ve hata işleme sağlar.
 */

type FetchOptions = RequestInit & {
  cache?: RequestCache;
  next?: {
    revalidate?: number | false;
    tags?: string[];
  };
}

// API istekleri için genel fonksiyon
export async function fetchAPI<T>(
  endpoint: string, 
  options: FetchOptions = {}
): Promise<T> {
  const isBrowser = typeof window !== 'undefined';
  const baseUrl = isBrowser ? '/api' : (process.env.NEXT_PUBLIC_API_URL || '/api');
  
  try {
    const response = await fetch(`${baseUrl}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      cache: options.cache || 'no-store', // Varsayılan olarak önbelleğe alma
      next: options.next || { revalidate: 60 }, // 1 dakika
      ...options
    });

    if (!response.ok) {
      throw new Error(`API isteği başarısız oldu: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    return data as T;
  } catch (error) {
    console.error(`API isteği sırasında hata oluştu: ${endpoint}`, error);
    throw error;
  }
}

// Ürünleri getir
export async function fetchProducts(
  page = 1, 
  limit = 10, 
  category?: string, 
  isFeatured?: boolean,
  search?: string,
  sort?: string
) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('limit', limit.toString());
  
  if (category) {
    params.append('category', category);
  }
  
  if (isFeatured !== undefined) {
    params.append('isFeatured', isFeatured.toString());
  }
  
  if (search) {
    params.append('search', search);
  }
  
  if (sort) {
    params.append('sort', sort);
  }
  
  return fetchAPI<{
    success: boolean;
    data: {
      products: any[];
      pagination: {
        total: number;
        totalPages: number;
        currentPage: number;
        limit: number;
      }
    }
  }>(`/products?${params.toString()}`, {
    next: { 
      revalidate: 3600, // 1 saat önbellek
      tags: ['products']
    }
  });
}

// Tek ürün getir
export async function fetchProductBySlug(slug: string) {
  return fetchAPI<{
    success: boolean;
    data: any;
  }>(`/products/${slug}`, {
    next: { 
      revalidate: 3600, // 1 saat önbellek
      tags: ['product', `product-${slug}`]
    }
  });
}

// Kategorileri getir
export async function fetchCategories() {
  return fetchAPI<{
    success: boolean;
    data: any[];
  }>('/categories', {
    next: { 
      revalidate: 86400, // 24 saat önbellek
      tags: ['categories']
    }
  });
}

// İletişim formu gönderimi
export async function submitContactForm(formData: any) {
  return fetchAPI<{
    success: boolean;
    message: string;
  }>('/contact', {
    method: 'POST',
    body: JSON.stringify(formData),
    next: { revalidate: false } // Önbelleğe almadan
  });
} 